import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';

export interface RunState {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
}

const RUN_DURATION_MS = 2200;

// Once three tasks are running at once, every other idle control disables itself with a reason
// until one finishes. This is a client-simulated ceiling over this same store, not a real
// account-wide limit (that depends on a platform-side capacity check that doesn't exist yet) —
// it mirrors the mockup's own local running-count check.
export const RUN_CAP = 3;

const runStates = new Map<string, RunState>();

// Tracks how many tasks are currently running, kept in step with the two places below that flip
// isRunning, rather than scanning the whole map on every read — the map only grows across a
// session and this count is read on every mounted control's mount and on every state change.
let runningCount = 0;

function readState(taskId: string): RunState {
	return runStates.get(taskId) ?? {hasEverRun: false, isRunning: false, outputRead: false};
}

function writeState(taskId: string, next: RunState): void {
	runStates.set(taskId, next);
	dispatch(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, taskId);
}

export function getRunState(taskId: string): RunState {
	return readState(taskId);
}

export function getRunningCount(): number {
	return runningCount;
}

export function isAtRunCap(): boolean {
	return runningCount >= RUN_CAP;
}

export function invoke(taskId: string, agentId?: string): void {
	// agentId is accepted now — a later caller needs to pass which agent was picked — but this
	// simulation does not yet vary behavior by agent; every run completes the same way.
	void agentId;
	const current = readState(taskId);
	// A second call for a task already mid-run would otherwise start a second timer; that timer's
	// completion later overwrites state unconditionally and could silently revert a read output
	// back to unread. No caller triggers this today, but the guard costs nothing.
	if (current.isRunning) return;
	// The cap is also enforced presentationally in deriveControlState (every other idle control
	// disables once isAtRunCap() is true), but that's the UI's rendering of the invariant, not
	// the invariant itself — NovaAgentPickerForTask calls invoke() directly, bypassing that
	// disabled-button gate entirely. Not reachable today (the caret that opens the picker is
	// hidden outside the 'action' state), but the store, not the UI, is where this belongs once
	// a second call site exists.
	if (isAtRunCap()) return;
	runningCount += 1;
	writeState(taskId, {...current, isRunning: true});
	// No run service exists yet, so this timer stands in for one. Swapping in a real network call
	// still means replacing this body — but unlike this simulation, a real call can fail, and
	// nothing here models that yet: RunState has no error field and ControlState's 'failed' state
	// is reserved, unimplemented. Adding real failure handling touches every layer between here
	// and the rendered control, not just this function.
	setTimeout(() => {
		runningCount -= 1;
		writeState(taskId, {hasEverRun: true, isRunning: false, outputRead: false});
	}, RUN_DURATION_MS);
}

export function markRead(taskId: string): void {
	const current = readState(taskId);
	if (!current.hasEverRun || current.isRunning) return;
	writeState(taskId, {...current, outputRead: true});
}

export function subscribeToRunState(taskId: string, callback: () => void): () => void {
	const handler = (changedTaskId: string): void => {
		if (changedTaskId === taskId) callback();
	};
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

// The run cap and the row-highlight-on-unread-Ready feature are both properties of the whole
// tracked set, not of one taskId — a plain per-task subscription can't tell an idle task that a
// caller renders elsewhere just now hit (or dropped below) the ceiling.
export function subscribeToAnyRunStateChange(callback: () => void): () => void {
	const handler = (): void => callback();
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

export function isUnreadReady(taskId: string): boolean {
	const state = readState(taskId);
	return state.hasEverRun && !state.isRunning && !state.outputRead;
}
